import { useState, useMemo } from 'react'
import {
  FlaskConical,
  ChevronDown,
  Check,
  AlertCircle,
  FileText,
  Eye,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs'
import {
  useDecisionMatrix,
  useLabEvaluation,
  useSaveLabEvaluation,
  useCompleteLabEvaluation,
  type DecisionMatrix,
} from '@/admin/hooks/use-lab-tasks'

// PR #395 assimilation Phase B - frontend cutover TODO:
// ``useDecisionMatrix`` currently reads the bespoke ``DecisionMatrix*``
// Django tree via ``/api/claims/decision-matrices/<key>/``. The
// framework-compliant path is the new rule-driven endpoints exposed
// by ``apps.claims.views.lab.LabResolutionMatricesView`` /
// ``LabResolutionEvaluateView`` (see ``useLabResolutionMatrices`` and
// ``useEvaluateLabResolution`` in ``use-lab-tasks.ts``). Outcome
// resolution becomes a ``POST .../lab-resolution/evaluate/`` with
// ``{defect_type, sub_type_key, bracket_key}`` returning the matched
// ``ResolutionRule.outcome`` + metadata, rather than the tech walking
// the cells client-side. The matrix grouping for the SW001..SW033
// header view comes from ``GET .../lab-resolution/matrices/``.

const OUTCOME_COLORS: Record<string, string> = {
  APPROVE: 'bg-[var(--status-ok-bg)] text-[var(--status-ok)] border-[var(--status-ok-border)]',
  DENY: 'bg-[var(--red)]/10 text-[var(--red)] border-[var(--red)]/30',
  GOODWILL: 'bg-[#e67e22]/10 text-[#e67e22] border-[#e67e22]/30',
  PROCESS_DEFECTIVE: 'bg-[color-mix(in_srgb,var(--green)_10%,transparent)] text-[var(--green)] border-[color-mix(in_srgb,var(--green)_30%,transparent)]',
  NA: 'bg-[var(--bg-subtle)] text-[var(--ink-3)] border-[var(--rule)]',
}

const OUTCOME_LABELS: Record<string, string> = {
  APPROVE: 'Approve',
  DENY: 'Deny',
  GOODWILL: 'Goodwill',
  PROCESS_DEFECTIVE: 'Process Defective',
  NA: 'N/A',
}

interface Props {
  claimUuid: string
  taskUuid: string
  matrixKey: string | null | undefined
  bracketKey?: string
  readOnly?: boolean
}

export function DecisionMatrixWorkspace({
  claimUuid,
  taskUuid,
  matrixKey,
  bracketKey,
  readOnly = false,
}: Props) {
  const { data: matrix, isLoading: matrixLoading } = useDecisionMatrix(matrixKey)
  const { data: evaluation } = useLabEvaluation(claimUuid, taskUuid)
  const saveEval = useSaveLabEvaluation(claimUuid, taskUuid)
  const completeEval = useCompleteLabEvaluation(claimUuid, taskUuid)

  const [selectedRowUuid, setSelectedRowUuid] = useState<string | null>(null)
  const [selectedCellUuid, setSelectedCellUuid] = useState<string | null>(null)
  const [observations, setObservations] = useState<Record<string, string>>({})
  const [ventilationAdequate, setVentilationAdequate] = useState<boolean | null>(null)
  const [additionalNotes, setAdditionalNotes] = useState('')


  const selectedRow = useMemo(
    () => matrix?.rows.find((r) => r.uuid === (selectedRowUuid ?? evaluation?.selected_row)),
    [matrix, selectedRowUuid, evaluation]
  )

  const selectedCell = useMemo(
    () => selectedRow?.cells.find((c) => c.uuid === (selectedCellUuid ?? evaluation?.selected_cell)),
    [selectedRow, selectedCellUuid, evaluation]
  )

  const evaluationSteps = useMemo(() => {
    const meta = matrix?.metadata as { evaluation_steps?: Array<{ step: number; title: string; description: string }> } | undefined
    return meta?.evaluation_steps ?? []
  }, [matrix])

  if (!matrixKey) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-light)] px-6 py-8 text-center">
        <FlaskConical className="h-5 w-5 text-[var(--ink-3)]" />
        <p className="text-[13px] text-[var(--ink-2)]">
          No decision matrix available for this defect type
        </p>
        <p className="text-[11px] text-[var(--ink-3)]">
          The major category must be set before a matrix can be loaded
        </p>
      </div>
    )
  }

  if (matrixLoading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] py-8">
        <p className="text-[12px] text-[var(--ink-3)]">Loading matrix…</p>
      </div>
    )
  }

  if (!matrix) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3">
        <AlertCircle className="h-4 w-4 text-[var(--red)]" />
        <p className="text-[12px] text-[var(--ink-2)]">Matrix not found</p>
      </div>
    )
  }

  const isComplete = !!evaluation?.completed_at

  const handleSave = () => {
    saveEval.mutate({
      matrix_uuid: matrix.uuid,
      selected_row_uuid: selectedRowUuid ?? evaluation?.selected_row ?? undefined,
      selected_cell_uuid: selectedCellUuid ?? evaluation?.selected_cell ?? undefined,
      bracket_key: bracketKey ?? evaluation?.bracket_key ?? '',
      outcome: selectedCell?.outcome ?? evaluation?.outcome ?? '',
      observations: { ...(evaluation?.observations ?? {}), ...observations },
      ventilation_adequate: ventilationAdequate ?? evaluation?.ventilation_adequate,
      additional_notes: additionalNotes || evaluation?.additional_notes || '',
    })
  }

  const handleComplete = () => {
    handleSave()
    setTimeout(() => completeEval.mutate(), 500)
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Matrix header */}
      <div className="flex items-center justify-between rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-3">
        <div className="flex items-center gap-2.5">
          <FlaskConical className="h-4 w-4 text-[var(--green)]" />
          <div>
            <p className="text-[13px] font-medium text-[var(--ink)]">{matrix.name}</p>
            <p className="text-[11px] text-[var(--ink-3)]">{matrix.description}</p>
          </div>
        </div>
        {isComplete && (
          <Badge variant="default" className="bg-[var(--status-ok)]">
            <Check className="mr-1 h-3 w-3" /> Complete
          </Badge>
        )}
      </div>

      <Tabs defaultValue="steps">
        <TabsList variant="line">
          <TabsTrigger value="steps">
            <FileText className="mr-1.5 h-3 w-3" />
            Evaluation Steps
          </TabsTrigger>
          <TabsTrigger value="matrix">
            <Eye className="mr-1.5 h-3 w-3" />
            Decision Matrix
          </TabsTrigger>
        </TabsList>

        {/* ── Evaluation Steps Tab ── */}
        <TabsContent value="steps">
          <div className="flex flex-col gap-2">
            {evaluationSteps.length === 0 ? (
              <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
                No evaluation steps defined for this matrix
              </p>
            ) : (
              evaluationSteps.map((step) => (
                <EvaluationStepCard
                  key={step.step}
                  step={step}
                  observation={observations[`step_${step.step}`] ?? evaluation?.observations?.[`step_${step.step}`] ?? ''}
                  onChange={(val) =>
                    setObservations((prev) => ({
                      ...prev,
                      [`step_${step.step}`]: val,
                    }))
                  }
                  readOnly={readOnly || isComplete}
                />
              ))
            )}

            {/* Ventilation check */}
            <div className="rounded-2xl border border-[var(--rule)] bg-[var(--bg)] px-5 py-4 shadow-[var(--shadow-card)]">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                Ventilation Assessment
              </p>
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-[var(--ink-2)]">
                  <input
                    type="radio"
                    name="ventilation"
                    checked={(ventilationAdequate ?? evaluation?.ventilation_adequate) === true}
                    onChange={() => setVentilationAdequate(true)}
                    disabled={readOnly || isComplete}
                    className="accent-[var(--green)]"
                  />
                  Adequate
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-[var(--ink-2)]">
                  <input
                    type="radio"
                    name="ventilation"
                    checked={(ventilationAdequate ?? evaluation?.ventilation_adequate) === false}
                    onChange={() => setVentilationAdequate(false)}
                    disabled={readOnly || isComplete}
                    className="accent-[var(--red)]"
                  />
                  Inadequate
                </label>
              </div>
            </div>

            {/* Additional notes */}
            <div className="rounded-2xl border border-[var(--rule)] bg-[var(--bg)] px-5 py-4 shadow-[var(--shadow-card)]">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                Additional Notes
              </p>
              <textarea
                value={additionalNotes || evaluation?.additional_notes || ''}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                disabled={readOnly || isComplete}
                placeholder="Any additional observations or notes…"
                rows={3}
                className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-2.5 py-2 text-[12px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--accent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
          </div>
        </TabsContent>

        {/* ── Matrix Grid Tab ── */}
        <TabsContent value="matrix">
          <MatrixGrid
            matrix={matrix}
            selectedRowUuid={selectedRowUuid ?? evaluation?.selected_row ?? null}
            selectedCellUuid={selectedCellUuid ?? evaluation?.selected_cell ?? null}
            onSelectRow={(uuid) => {
              if (!readOnly && !isComplete) {
                setSelectedRowUuid(uuid)
                setSelectedCellUuid(null)
              }
            }}
            onSelectCell={(uuid) => {
              if (!readOnly && !isComplete) setSelectedCellUuid(uuid)
            }}
            readOnly={readOnly || isComplete}
          />
        </TabsContent>
      </Tabs>

      {/* Outcome card */}
      {(selectedCell || evaluation?.outcome) && (
        <div
          className={cn(
            'rounded-2xl border-[0.5px] px-4 py-3',
            OUTCOME_COLORS[selectedCell?.outcome ?? evaluation?.outcome ?? 'NA']
          )}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.08em] opacity-70">
                Matrix Determination
              </p>
              <p className="mt-0.5 text-[16px] font-semibold">
                {OUTCOME_LABELS[selectedCell?.outcome ?? evaluation?.outcome ?? ''] ?? 'Unknown'}
              </p>
              {selectedCell?.conditions && (
                <p className="mt-1 text-[11px] opacity-80">
                  {selectedCell.conditions}
                </p>
              )}
            </div>
            {selectedRow && (
              <div className="text-right">
                <p className="text-[10px] opacity-70">Sub-type</p>
                <p className="text-[12px] font-medium">
                  {selectedRow.sub_type_label}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!readOnly && !isComplete && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={saveEval.isPending}
          >
            {saveEval.isPending ? 'Saving…' : 'Save Progress'}
          </Button>
          <Button
            size="sm"
            onClick={handleComplete}
            disabled={
              completeEval.isPending ||
              !(selectedCell?.outcome ?? evaluation?.outcome)
            }
            className="bg-[var(--green)] text-white hover:bg-[var(--green-hover)]"
          >
            {completeEval.isPending
              ? 'Completing…'
              : 'Confirm Determination'}
          </Button>
        </div>
      )}
    </div>
  )
}

function EvaluationStepCard({
  step,
  observation,
  onChange,
  readOnly,
}: {
  step: { step: number; title: string; description: string }
  observation: string
  onChange: (value: string) => void
  readOnly: boolean
}) {
  const [open, setOpen] = useState(true)
  return (
    <div className="rounded-2xl border border-[var(--rule)] bg-[var(--bg)] shadow-[var(--shadow-card)]">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        className="flex h-auto w-full cursor-pointer items-center justify-start gap-3 rounded-2xl px-5 py-3.5 hover:bg-transparent aria-expanded:bg-transparent"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--green)_10%,transparent)] text-[14px] font-medium text-[var(--green)]">
          {step.step}
        </span>
        <span className="flex-1 text-left text-[15px] font-medium whitespace-normal text-[var(--ink)]">
          {step.title}
        </span>
        {observation && (
          <Check className="h-4 w-4 text-[var(--green)]" />
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 text-[var(--ink-2)] transition-transform',
            open && 'rotate-180'
          )}
        />
      </Button>
      {open && (
        <div className="border-t-[0.5px] border-[var(--rule-light)] px-5 py-4">
          <p className="mb-2.5 text-[14px] text-[var(--ink-2)]">
            {step.description}
          </p>
          <textarea
            value={observation}
            onChange={(e) => onChange(e.target.value)}
            disabled={readOnly}
            placeholder="Record your observations…"
            rows={2}
            className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-2.5 py-2 text-[12px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--accent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          />
        </div>
      )}
    </div>
  )
}

function MatrixGrid({
  matrix,
  selectedRowUuid,
  selectedCellUuid,
  onSelectRow,
  onSelectCell,
  readOnly,
}: {
  matrix: DecisionMatrix
  selectedRowUuid: string | null
  selectedCellUuid: string | null
  onSelectRow: (uuid: string) => void
  onSelectCell: (uuid: string) => void
  readOnly: boolean
}) {
  if (!matrix.rows.length) {
    return (
      <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
        No rows in this matrix
      </p>
    )
  }

  const brackets = matrix.rows[0]?.cells.map((c) => ({
    key: c.bracket_key,
    label: c.bracket_label,
  })) ?? []

  return (
    <div className="overflow-x-auto rounded-2xl border-[0.5px] border-[var(--rule)]">
      <table className="w-full text-left text-[11px]">
        <thead>
          <tr className="border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
            <th className="px-3 py-2 font-medium text-[var(--ink-3)]">Type</th>
            <th className="px-3 py-2 font-medium text-[var(--ink-3)]">Criteria</th>
            {brackets.map((b) => (
              <th
                key={b.key}
                className="px-3 py-2 text-center font-medium text-[var(--ink-3)]"
              >
                {b.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((row) => {
            const isRowSelected = row.uuid === selectedRowUuid
            return (
              <tr
                key={row.uuid}
                className={cn(
                  'border-b-[0.5px] border-[var(--rule)] transition-colors',
                  isRowSelected
                    ? 'bg-[color-mix(in_srgb,var(--green)_5%,transparent)]'
                    : 'hover:bg-[var(--bg-light)]',
                  !readOnly && 'cursor-pointer'
                )}
                onClick={() => onSelectRow(row.uuid)}
              >
                <td className="px-3 py-2.5 font-medium text-[var(--ink)]">
                  {row.sub_type_label}
                  {row.visual_boundary && (
                    <span className="ml-1 text-[10px] text-[var(--ink-3)]">
                      ({row.visual_boundary})
                    </span>
                  )}
                </td>
                <td className="max-w-[200px] px-3 py-2.5 text-[var(--ink-2)]">
                  {row.criteria}
                </td>
                {row.cells.map((cell) => {
                  const isCellSelected =
                    isRowSelected && cell.uuid === selectedCellUuid
                  return (
                    <td
                      key={cell.uuid}
                      className={cn(
                        'px-3 py-2.5 text-center',
                        isCellSelected && 'ring-2 ring-[var(--green)] ring-inset rounded-[4px]'
                      )}
                      onClick={(e) => {
                        e.stopPropagation()
                        onSelectRow(row.uuid)
                        onSelectCell(cell.uuid)
                      }}
                    >
                      <span
                        className={cn(
                          'inline-block rounded-[4px] border-[0.5px] px-2 py-0.5 text-[10px] font-medium',
                          OUTCOME_COLORS[cell.outcome]
                        )}
                      >
                        {OUTCOME_LABELS[cell.outcome]}
                      </span>
                      {cell.conditions && (
                        <p className="mt-0.5 text-[9px] text-[var(--ink-3)]">
                          {cell.conditions}
                        </p>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
